import type { InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import type { AnyValueMap } from '@opentelemetry/api-logs';
import { emitInfoLog } from './logger';
import { Logger } from 'utils/logger';

const MAX_PAYLOAD_BYTES = 4096;

const SKIP_LOGGING_ENDPOINTS = [
  '/v1.1/token/refresh/',
  '/v1.1/logout/',
  '/v1.1/token/',
];

const SENSITIVE_KEYS = new Set([
  'password',
  'token',
  'access',
  'refresh',
  'authorization',
  'Authorization',
]);

const ID_KEY_SUFFIX_REGEX = /(_id|_pk)$/;

// Extracts _id / _pk keys from query params and request body only.
// Path IDs are already visible via http.endpoint in attrs.
export function extractIdsFromRequest(
  config: InternalAxiosRequestConfig,
): AnyValueMap {
  const result: AnyValueMap = {};

  // Query string — keys ending in _id / _pk
  const params = config.params as Record<string, unknown> | undefined;
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (
        ID_KEY_SUFFIX_REGEX.test(key) &&
        (typeof value === 'string' || typeof value === 'number')
      ) {
        result[key] = String(value);
      }
    }
  }

  // Request body — keys ending in _id / _pk
  const body = parseBody(config.data);
  if (body && typeof body === 'object') {
    for (const [key, value] of Object.entries(
      body as Record<string, unknown>,
    )) {
      if (
        ID_KEY_SUFFIX_REGEX.test(key) &&
        (typeof value === 'string' || typeof value === 'number')
      ) {
        result[key] = String(value);
      }
    }
  }

  return result;
}

// Returns the parsed, redacted payload as a structured AnyValueMap so it is
// directly readable in SigNoz. Returns undefined if the payload is too large,
// unserializable, or a binary type (FormData / Blob).
export function sanitizePayload(data: unknown): AnyValueMap | undefined {
  if (data == null) return undefined;
  if (data instanceof FormData) return undefined;
  if (data instanceof Blob) return undefined;

  const parsed = parseBody(data);
  if (parsed == null || typeof parsed !== 'object' || Array.isArray(parsed))
    return undefined;

  // Discard oversized payloads entirely rather than truncating mid-JSON
  const serialized = safeStringify(parsed);
  if (serialized == null || serialized.length > MAX_PAYLOAD_BYTES)
    return undefined;

  const redacted = redactSensitiveKeys(parsed) as Record<string, unknown>;
  return toAnyValueMap(redacted);
}

export function logApiRequest(config: InternalAxiosRequestConfig): void {
  try {
    const method = (config.method ?? 'get').toLowerCase();

    // Only log mutation requests — GET carries no body and is high-volume
    if (method === 'get') return;

    const url = config.url ?? '';

    if (SKIP_LOGGING_ENDPOINTS.some((endpoint) => url.includes(endpoint)))
      return;

    const httpMethod = method.toUpperCase();
    const payload = sanitizePayload(config.data);
    const extractedIds = extractIdsFromRequest(config);

    emitInfoLog({
      body: {
        message: `HTTP Request: ${httpMethod} ${url}`,
        ...(payload ? { payload } : {}),
      },
      attrs: {
        source: 'AxiosInterceptor',
        level: 'info',
        'http.endpoint': url,
        'http.method': httpMethod,
        ...extractedIds,
      },
    });
  } catch (error) {
    Logger.error({ message: 'logApiRequest failed', data: error });
  }
}

export function logApiResponse(response: AxiosResponse): void {
  try {
    const config = response?.config;
    const method = (config?.method ?? 'get')?.toLowerCase();

    if (method === 'get') return;
    if (config.responseType === 'blob') return;

    const url = config.url ?? '';

    if (SKIP_LOGGING_ENDPOINTS.some((endpoint) => url.includes(endpoint)))
      return;

    const httpMethod = method.toUpperCase();
    const payload = sanitizePayload(response.data);

    emitInfoLog({
      body: {
        message: `HTTP Response: ${response.status} ${httpMethod} ${url}`,
        ...(payload ? { payload } : {}),
      },
      attrs: {
        source: 'AxiosInterceptor',
        level: 'info',
        'http.endpoint': url,
        'http.method': httpMethod,
        'http.status': response.status,
      },
    });
  } catch (error) {
    Logger.error({ message: 'logApiResponse failed', data: error });
  }
}

// --- internal helpers ---

function parseBody(data: unknown): unknown {
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return data;
    }
  }
  return data;
}

function redactSensitiveKeys(data: unknown): unknown {
  if (data == null || typeof data !== 'object' || Array.isArray(data)) {
    return data;
  }
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
    result[key] = SENSITIVE_KEYS.has(key) ? '[REDACTED]' : value;
  }
  return result;
}

// Converts a plain object to AnyValueMap.
// Primitive values (string, number, boolean) pass through directly so they
// are readable as structured fields in SigNoz. Nested objects are
// JSON-stringified to satisfy the AnyValue constraint.
function toAnyValueMap(obj: Record<string, unknown>): AnyValueMap {
  const result: AnyValueMap = {};
  for (const [key, value] of Object.entries(obj)) {
    if (
      value == null ||
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      result[key] = value as AnyValueMap[string];
    } else {
      result[key] = safeStringify(value) ?? '[unserializable]';
    }
  }
  return result;
}

function safeStringify(data: unknown): string | undefined {
  try {
    return JSON.stringify(data);
  } catch {
    return undefined;
  }
}
